/**
 * Primitive colour ramps. Names describe the value, never its use — `neutral.500`,
 * never `text.muted`. The semantic layer in `packages/ui` does the mapping, and that
 * indirection is what makes a reskin a token edit rather than a component rewrite.
 *
 * This palette is a deliberate placeholder. Visual direction is a product decision
 * and is deferred (docs/decisions/deferred.md); a palette that looked designed would
 * be mistaken for one. It is a neutral grey ramp plus one restrained accent, sized so
 * that swapping in a real brand is an edit to this file and nothing else.
 *
 * Ramps run 50 (lightest) to 950 (darkest) so a dark theme can invert by index.
 */
export const color = {
    neutral: {
        50: '#f8f9fa',
        100: '#f1f3f5',
        200: '#e9ecef',
        300: '#dee2e6',
        400: '#ced4da',
        500: '#adb5bd',
        600: '#868e96',
        700: '#495057',
        800: '#343a40',
        900: '#212529',
        950: '#141719',
    },
    /** The single accent. Deliberately unremarkable — see the note above. */
    accent: {
        50: '#eef4fb',
        100: '#d8e6f6',
        200: '#b3cdec',
        300: '#84ade0',
        400: '#5289d1',
        500: '#316bb8',
        600: '#265596',
        700: '#1f4478',
        800: '#1a3760',
        900: '#162d4e',
        950: '#0e1c31',
    },
    success: {
        50: '#eaf6ee',
        100: '#cbe9d5',
        300: '#82c99b',
        500: '#3f9d63',
        700: '#2c6f46',
        900: '#1c4a2e',
    },
    warning: {
        50: '#fdf4e6',
        100: '#fae3bd',
        300: '#f2c069',
        500: '#d99a22',
        700: '#9c6d14',
        900: '#65470c',
    },
    danger: {
        50: '#fbecec',
        100: '#f5cfcf',
        300: '#e59191',
        500: '#cb4f4f',
        700: '#993535',
        900: '#652222',
    },
    info: {
        50: '#eaf3f8',
        100: '#c9e2ef',
        300: '#8ac2dd',
        500: '#4a9bc4',
        700: '#316d8d',
        900: '#20495d',
    },
    /**
     * Subject families, transcribed from `design/tokens.css`.
     *
     * Cool carries subject; warm carries state (`009` §4). The two never share a role,
     * which is what keeps Finance — about 40 degrees from the success green —
     * unambiguous.
     *
     * Split by theme rather than one ramp, because the surfaces are blended to solid
     * colours in opposite directions: React Native cannot stack a translucent wash, so
     * a dark tint and a light tint are two values rather than one at two opacities.
     */
    subject: {
        dark: {
            growth: {
                hue: '#b49dfb',
                mid: '#8b5cf6',
                surface: '#261e3b',
                raised: '#342852',
                edge: '#6145a0',
            },
            finance: {
                hue: '#7dd3fc',
                mid: '#38bdf8',
                surface: '#1b2b3b',
                raised: '#253a52',
                edge: '#347aa2',
            },
            people: {
                hue: '#f0abfc',
                mid: '#e879f9',
                surface: '#32223b',
                raised: '#452e53',
                edge: '#9455a2',
            },
        },
        light: {
            growth: {
                hue: '#6d28d9',
                mid: '#8b5cf6',
                surface: '#eee5fd',
                raised: '#ddcefb',
                edge: '#aa83e1',
            },
            finance: {
                hue: '#03649a',
                mid: '#0ea5e9',
                surface: '#deeff8',
                raised: '#c6dcf4',
                edge: '#67accc',
            },
            people: {
                hue: '#9d1baa',
                mid: '#d946ef',
                surface: '#f7e3f9',
                raised: '#eacaf6',
                edge: '#d078d3',
            },
        },
    },
    /**
     * The two shared illustration materials, transcribed from `design/tokens.css`.
     *
     * `009` §5: illustration materials and state colours are **disjoint sets** — no
     * state colour appears in an illustration and no illustration material appears on
     * a control. That rule is what lets brass exist at all while warm stays reserved
     * for state everywhere else.
     */
    illustration: {
        dark: {
            metal: '#b6bcc8',
            metalDeep: '#6b7280',
            brass: '#e8c170',
            brassDeep: '#a97f2e',
        },
        light: {
            metal: '#9aa2b1',
            metalDeep: '#5b6472',
            brass: '#d4a744',
            brassDeep: '#8a6620',
        },
    },
    /**
     * Skin and hair for the drawn cast, wide enough for the whole of it. Three tones
     * each, ordered by lightness in both schemes, because a flat-faced figure needs a
     * lit top, a face and a shade the way `box()` does. Dark is a step down so a face
     * does not glare off a dark surface. Warm by necessity: like brass, these are an
     * illustration material and never stand for state. Mouth and blush are shared by
     * every face, so they are one value each rather than a range. `wear` is the clothes
     * the subject families have no hue for, so each character owns a colour. `soft` is the
     * same owned colours in pastel, plus a cream and a navy, for the editorial cast.
     */
    cast: {
        light: {
            skin: {
                pale: { lit: '#fbe6d8', face: '#f6d7c3', shade: '#e3b59b' },
                light: { lit: '#f6d2b7', face: '#eec1a0', shade: '#d79e7a' },
                medium: { lit: '#e6b590', face: '#d9a07a', shade: '#bd8058' },
                tan: { lit: '#c98e66', face: '#b97b52', shade: '#9a603b' },
                brown: { lit: '#a0673f', face: '#8d5634', shade: '#713f22' },
                deep: { lit: '#71452a', face: '#5e3820', shade: '#462714' },
            },
            hair: {
                silver: { lit: '#e6e8ec', face: '#d0d4db', shade: '#a3aab5' },
                black: { lit: '#4a413d', face: '#2a2523', shade: '#151211' },
                brown: { lit: '#86583f', face: '#6b4430', shade: '#4d2f20' },
                auburn: { lit: '#be5d3a', face: '#a2482a', shade: '#7a321b' },
                blonde: { lit: '#efd291', face: '#e2bd6e', shade: '#c09a4c' },
                ginger: { lit: '#f5a65b', face: '#e38431', shade: '#b9601c' },
            },
            wear: {
                forest: { lit: '#6fbf86', face: '#3d9a5c', shade: '#2a7043' },
                wine: { lit: '#c4566e', face: '#9e2f4a', shade: '#741f35' },
                mustard: { lit: '#f2cc5a', face: '#e0aa2a', shade: '#b5841a' },
            },
            soft: {
                finance: { lit: '#c9dff2', face: '#9cc3e6', shade: '#6f9fcb' },
                growth: { lit: '#ddd0f7', face: '#b9a3ec', shade: '#8d74cf' },
                people: { lit: '#f6c6ec', face: '#e796d6', shade: '#c46ab2' },
                forest: { lit: '#c7e6cf', face: '#96cfa6', shade: '#66a87a' },
                wine: { lit: '#f2c4cc', face: '#e39aa8', shade: '#c06c7f' },
                mustard: { lit: '#fbe3a0', face: '#f5cd6b', shade: '#d9a93f' },
                cream: { lit: '#fbf5ea', face: '#f3e9d6', shade: '#dccdb2' },
                navy: { face: '#34405c', shade: '#232c42' },
            },
            mouth: { inside: '#6b2121', tongue: '#f07f7f' },
            blush: '#f5a3a0',
        },
        dark: {
            skin: {
                pale: { lit: '#ecd2c1', face: '#e3c3ae', shade: '#cba08a' },
                light: { lit: '#e6bf9f', face: '#dbae8e', shade: '#c28a68' },
                medium: { lit: '#d59f7c', face: '#c78f6b', shade: '#a9704b' },
                tan: { lit: '#b87d56', face: '#a86e48', shade: '#8a5533' },
                brown: { lit: '#905a39', face: '#7f4c2e', shade: '#653720' },
                deep: { lit: '#673e29', face: '#553220', shade: '#3e2213' },
            },
            hair: {
                silver: { lit: '#d3d6dc', face: '#bfc3cb', shade: '#929aa5' },
                black: { lit: '#574d48', face: '#3a3330', shade: '#211c1a' },
                brown: { lit: '#90614a', face: '#7a5039', shade: '#583826' },
                auburn: { lit: '#b85b3c', face: '#a24b2e', shade: '#7c3520' },
                blonde: { lit: '#e0c37f', face: '#d4b065', shade: '#b08e45' },
                ginger: { lit: '#e09a55', face: '#cc7a30', shade: '#a2581d' },
            },
            wear: {
                forest: { lit: '#64ad79', face: '#3f8a56', shade: '#2c633e' },
                wine: { lit: '#b85068', face: '#952f48', shade: '#6c2034' },
                mustard: { lit: '#dfba52', face: '#c99a2a', shade: '#a1771c' },
            },
            soft: {
                finance: { lit: '#b4cde3', face: '#88b0d4', shade: '#5d8bb6' },
                growth: { lit: '#c8bce6', face: '#a48fd9', shade: '#7a62b9' },
                people: { lit: '#e2b2d8', face: '#d283c2', shade: '#ad5a9d' },
                forest: { lit: '#b2d3ba', face: '#82bb92', shade: '#57946a' },
                wine: { lit: '#ddb0b9', face: '#cd8796', shade: '#a95c6e' },
                mustard: { lit: '#e8d08f', face: '#ddb85c', shade: '#bf9234' },
                cream: { lit: '#e2dccf', face: '#d6cbb6', shade: '#bcac90' },
                navy: { face: '#3e4a6a', shade: '#2a3450' },
            },
            mouth: { inside: '#521818', tongue: '#d96c6c' },
            blush: '#d98b88',
        },
    },
    /**
     * The CAST-04 test's one colour `cast` cannot give it: the mouth of the editorial
     * faces, a single value, where `cast.light.mouth` is the open-mouth pair the other
     * style drew. The rest of the test cast is drawn in `cast` and `illustration`.
     */
    castTest: {
        mouth: '#3d1212',
    },
    /**
     * The cast the owner locked on 2026-10-11, in the colours its Recraft drawings use,
     * one block per character. Exact rather than mapped onto `cast`: the approval was of
     * the drawings, colours included. Betty's eyes and skirt share the one navy.
     */
    nibble: {
        betty: {
            skin: '#fea55b',
            skinShade: '#dd792a',
            nose: '#e98943',
            hair: '#c0bebb',
            bun: '#8c8c8c',
            blouse: '#284fe7',
            blouseShade: '#001ba4',
            navy: '#001c3c',
            shoe: '#be4f0a',
            teeth: '#ffffff',
        },
    },
    /**
     * Data series — one group per job a reader has, and the first values in this
     * file with no mockup behind them, because `design/` has no chart.
     *
     * **Cool only, and never a subject value.** `009` §9 rule 3 reserves warm for
     * state, which is the rule a data ramp breaks first.
     *
     * Why each group is shaped as it is, why six is the cap, and which contrast bar
     * each is held to: `docs/decisions/data-colour.md`.
     */
    data: {
        dark: {
            sequential: ['#1e3a8a', '#2f5fc4', '#5b8def', '#94b8f8', '#c7dcfd'],
            diverging: {
                low: '#2dd4bf',
                mid: '#98a3b3',
                high: '#a78bfa',
            },
            categorical: [
                '#2dd4bf',
                '#56b6f0',
                '#8aa9fa',
                '#c39bf7',
                '#ef9fdb',
                '#a3b1c2',
            ],
            zone: {
                low: '#16302f',
                mid: '#1f242b',
                high: '#271f3a',
            },
        },
        light: {
            sequential: ['#dbe8fb', '#a3c2f2', '#5b8def', '#1d4ed8', '#152f7a'],
            diverging: {
                low: '#0f766e',
                mid: '#5c6675',
                high: '#5b21b6',
            },
            categorical: [
                '#0f766e',
                '#0369a1',
                '#3730a3',
                '#6b21a8',
                '#a21caf',
                '#475569',
            ],
            zone: {
                low: '#e4f1ef',
                mid: '#eceff3',
                high: '#f0e8fa',
            },
        },
    },
    /** Orange, and orange is the streak's alone. Distinct from `warning`. */
    streak: {
        dark: '#ff8a3d',
        light: '#c2410c',
    },
    /**
     * The mascot, transcribed from the icon art rather than derived from a ramp.
     *
     * It is its own family because a mascot is a brand mark: it must look the same
     * in both themes and must not shift when the accent does. The four gradient
     * pairs are moods, not decoration — `hot` is anger, `cool` is a lost streak,
     * `gold` is a level-up — and each keeps the same light-to-deep relationship so
     * a mood change interpolates instead of cutting.
     */
    mascot: {
        body: { light: '#e774f5', deep: '#6a2afc' },
        hot: { light: '#ff9a5c', deep: '#e0245e' },
        cool: { light: '#8fa8d8', deep: '#4658a8' },
        gold: { light: '#ffe07a', deep: '#f59e0b' },
        /**
         * The visor, the eyes inside it, and the catchlight on each eye.
         *
         * Pure black and pure white on purpose. The art samples as a near-black navy
         * and an off-white, but at the sizes a mascot renders that reads as a muddy
         * version of the intent rather than as a colour choice.
         */
        visor: '#ffffff',
        eye: '#000000',
        spark: '#ffffff',
        tear: '#8fd3ff',
    },
    /** True black and white. Never use these directly for text or surfaces. */
    base: {
        black: '#000000',
        white: '#ffffff',
        transparent: 'transparent',
    },
};
//# sourceMappingURL=color.js.map